/**
 * Death sequence state machine — orchestrates the full player death and respawn
 * choreography: iris close, corpse sprite, shatter/coalesce particles, camera fades,
 * enemy shockwave, and respawn with spawn protection.
 */
import Phaser from 'phaser';
import type { Player } from '../entities/Player';
import type { EnemyManager } from './EnemyManager';
import type { KnockbackSystem } from './KnockbackSystem';
import type { ScreenEffects } from './ScreenEffects';
import type { DeathParticleEffect } from '../effects/DeathParticleEffect';
import type { DeathIrisEffect } from '../effects/DeathIrisEffect';
import type { WallPhysicsSystem } from './WallPhysicsSystem';
import type { BodyGeometry } from './WallPhysicsSystem';
import type { SaveSystem, RunState } from './SaveSystem';
import type { Position } from '../utils/Types';
import { ConfigLoader } from './ConfigLoader';
import { DebugLog } from '../debug/DebugLog';
import { ensureCircleTexture, ensureSpikeTexture, crispText } from '../ui/CrispUtil';
import { magnitude, randomAngle } from '../utils/Math';
import { GAME_WIDTH, GAME_HEIGHT, SF0_X, SF0_Y, CURRENT_ZONE } from '../utils/Constants';
import { Font } from '../ui/Theme';

/** Death state transitions emitted to the owning scene. */
export type DeathState = 'alive' | 'dying' | 'dead' | 'respawning';

/** Dependencies injected by the owning scene — avoids coupling DeathSequence to GameScene. */
export interface DeathSequenceDeps {
  scene: Phaser.Scene;
  player: Player;
  enemyManager: EnemyManager;
  knockbackSystem: KnockbackSystem;
  screenEffects: ScreenEffects;
  deathParticles: DeathParticleEffect;
  deathIris: DeathIrisEffect;
  wallPhysics: WallPhysicsSystem;
  saveSystem: SaveSystem;
  saveName: string;
  getSpawnPoint: () => Position;
  getPlayerBodyGeometry: () => BodyGeometry | null;
  applyMaxHpPassive: () => void;
  onStateChange: (state: DeathState) => void;
}

/**
 * Encapsulates the full death-to-respawn sequence as a self-contained state machine.
 * Called once per death via `execute()`, which chains iris close, corpse rendering,
 * particle effects, camera fades, and respawn with spawn protection.
 */
export class DeathSequence {
  private readonly deps: DeathSequenceDeps;

  constructor(deps: DeathSequenceDeps) {
    this.deps = deps;
  }

  /** Run the full death sequence: iris close, shatter particles, fade, respawn, and coalesce. */
  execute(): void {
    const { scene, player, enemyManager, knockbackSystem, screenEffects,
      deathParticles, deathIris, wallPhysics, saveSystem } = this.deps;

    this.deps.onStateChange('dying');
    player.body.setVelocity(0, 0);

    // Deactivate all active skills before freezing — emits skill-deactivated so VFX orchestrator can clean up
    player.skillActivation.deactivateAll();

    const deathPos = { x: player.sprite.x, y: player.sprite.y };
    const corpseInfo = player.playDeath();

    // Drop a standalone corpse sprite at the death position — the real player
    // sprite is now free to be repositioned at the spawn point while the iris
    // closes on the corpse.
    const corpse = scene.add.sprite(deathPos.x, deathPos.y, 'player-sprite', corpseInfo.frame);
    corpse.setScale(corpseInfo.scaleX, corpseInfo.scaleY);
    corpse.setFlipX(corpseInfo.flipX);
    corpse.setAlpha(0.85);
    corpse.setDepth(10001); // Above iris (10000), visible through spotlight

    // Hide the real player sprite immediately
    player.sprite.setAlpha(0);

    const cfg = ConfigLoader.combat.screenEffects.playerDeath;

    // Save housekeeping — increment death count but don't clear the run.
    // The run state is re-saved after respawn with the new position and full HP.
    const meta = saveSystem.loadMeta();
    meta.totalDeaths++;
    saveSystem.saveMeta(meta);
    const deathCount = meta.totalDeaths;

    // Death flash — white burst radiating from the body
    const flashTex = ensureCircleTexture(scene, 32);
    const flash = scene.add.image(deathPos.x, deathPos.y, flashTex)
      .setTint(0xffffff)
      .setAlpha(cfg.deathFlashAlpha)
      .setDepth(10001)
      .setScale(0.5);
    scene.tweens.add({
      targets: flash,
      scaleX: cfg.deathFlashScale,
      scaleY: cfg.deathFlashScale,
      alpha: 0,
      duration: cfg.deathFlashMs,
      ease: 'Quad.easeOut',
      onComplete: () => flash.destroy(),
    });

    // Diffraction spikes — long shimmery rays locked to the circular burst
    const sp = cfg.deathSpikes;
    const spikeTex = ensureSpikeTexture(scene, sp.width, sp.texHeight);
    for (let i = 0; i < sp.count; i++) {
      const angle = Math.random() * Math.PI; // half-turn: each spike is double-ended
      const length = sp.minLength + Math.random() * (sp.maxLength - sp.minLength);
      const scaleY = length / sp.texHeight;

      const spike = scene.add.image(deathPos.x, deathPos.y, spikeTex)
        .setDepth(10001)
        .setRotation(angle)
        .setScale(1, scaleY)
        .setAlpha(cfg.deathFlashAlpha);

      // Alpha rides the flash's Quad.easeOut envelope — (1-t)² — with shimmer on top.
      // Single addCounter avoids competing alpha tweens.
      const shimmerPhase = Math.random() * Math.PI * 2;
      scene.tweens.addCounter({
        from: 0,
        to: 1,
        duration: cfg.deathFlashMs,
        onUpdate: (tween) => {
          const t = tween.getValue() ?? 0;
          const env = (1 - t) * (1 - t); // Quad.easeOut: matches the flash
          const shimmer = 1 - sp.shimmerDepth * (0.5 + 0.5 * Math.sin(
            shimmerPhase + t * sp.shimmerHz * Math.PI * 2,
          ));
          spike.setAlpha(cfg.deathFlashAlpha * env * shimmer);
        },
        onComplete: () => spike.destroy(),
      });

      // Scale expands with the flash, same duration and ease
      scene.tweens.add({
        targets: spike,
        scaleX: { from: 0.5, to: 1.5 },
        scaleY: { from: scaleY * 0.6, to: scaleY * 1.1 },
        duration: cfg.deathFlashMs,
        ease: 'Quad.easeOut',
      });
    }

    // Death shockwave — scatter nearby enemies in random directions
    DebugLog.log('combat', `DEATH SHOCKWAVE at (${deathPos.x.toFixed(0)},${deathPos.y.toFixed(0)}) radius=${cfg.deathShockwaveRadius} speed=${cfg.deathShockwaveSpeed} dur=${cfg.deathShockwaveDurationMs}`);
    for (const enemy of enemyManager.all) {
      const dx = enemy.sprite.x - deathPos.x;
      const dy = enemy.sprite.y - deathPos.y;
      const dist = magnitude(dx, dy);
      const body = enemy.sprite.body as Phaser.Physics.Arcade.Body | null;
      if (dist > cfg.deathShockwaveRadius) {
        DebugLog.log('combat', `  SKIP ${enemy.config.id} dist=${dist.toFixed(0)} > radius=${cfg.deathShockwaveRadius}`);
        continue;
      }

      const angle = randomAngle();
      let dirX = Math.cos(angle);
      let dirY = Math.sin(angle);

      // Flip direction away from any wall the enemy is already touching
      if (body) {
        if ((body.blocked.right && dirX > 0) || (body.blocked.left && dirX < 0)) dirX = -dirX;
        if ((body.blocked.down && dirY > 0) || (body.blocked.up && dirY < 0)) dirY = -dirY;
      }

      const dir = { x: dirX, y: dirY };
      DebugLog.log('combat', `  PUSH ${enemy.config.id} dist=${dist.toFixed(0)} dir=(${dirX.toFixed(2)},${dirY.toFixed(2)}) blocked=${body ? [body.blocked.left && 'L', body.blocked.right && 'R', body.blocked.up && 'U', body.blocked.down && 'D'].filter(Boolean).join('') || 'none' : 'nobody'} body.enable=${body?.enable}`);
      knockbackSystem.applyRaw(enemy, dir, cfg.deathShockwaveSpeed, cfg.deathShockwaveDurationMs);
    }

    // Stun all enemies for the full stun duration, then reset to duty
    for (const enemy of enemyManager.all) {
      enemy.ai.applyStagger(cfg.deathStunMs, true);
    }
    scene.time.delayedCall(cfg.deathStunMs, () => {
      for (const enemy of enemyManager.all) {
        enemy.ai.resetToDuty(enemy.sprite.x, enemy.sprite.y);
      }
    });

    // Rumble feedback while iris closes
    screenEffects.shake(cfg.rumbleIntensity, cfg.rumbleDurationMs);

    // Iris close -> vignette shrinks onto the corpse, enemies vanish under red
    deathIris.close(
      deathPos.x, deathPos.y,
      cfg.iris.closeDurationMs,
    ).then(() => {
      // Iris fully closed — let the body sit in the red for a beat
      scene.time.delayedCall(cfg.deathHoldMs, () => {
        // Fade corpse to white by layering a white silhouette over it
        const whiteCopy = scene.add.sprite(deathPos.x, deathPos.y, 'player-sprite', corpseInfo.frame);
        whiteCopy.setScale(corpseInfo.scaleX, corpseInfo.scaleY);
        whiteCopy.setFlipX(corpseInfo.flipX);
        whiteCopy.setTintFill(0xffffff);
        whiteCopy.setAlpha(0);
        whiteCopy.setDepth(10001);

        scene.tweens.add({
          targets: whiteCopy,
          alpha: 1,
          duration: cfg.dissolveFlashMs,
          onComplete: () => {
            corpse.destroy();
            // Hold the white silhouette, then shatter
            scene.time.delayedCall(cfg.dissolveFlashMs, () => {
              whiteCopy.setAlpha(0);
              // Particles fly over the iris (white on red = dramatic)
              deathParticles.shatter(deathPos.x, deathPos.y, 10002).then(() => {
                whiteCopy.destroy();
                this.deps.onStateChange('dead');

                // Manual black overlay instead of camera fadeOut — lets the
                // resurrection counter sit above it from the very start.
                const blackOverlay = scene.add.rectangle(
                  SF0_X + GAME_WIDTH / 2, SF0_Y + GAME_HEIGHT / 2,
                  GAME_WIDTH, GAME_HEIGHT, 0x000000,
                )
                  .setScrollFactor(0)
                  .setDepth(10002)
                  .setOrigin(0.5)
                  .setAlpha(0);

                const fadeInMs = cfg.fadeToBlackMs * cfg.fadeMultiplier;
                scene.tweens.add({
                  targets: blackOverlay,
                  alpha: 1,
                  duration: fadeInMs,
                  ease: 'Quad.easeIn',
                  onComplete: () => deathIris.remove(),
                });
                // Tell UIScene to fade out in sync with the overlay
                scene.events.emit('death-overlay-in', { fadeInMs, ease: 'Quad.easeIn' });
                // Start the hold timer immediately — it runs alongside
                // the slower fade-in so total sequence length stays the same.
                scene.events.emit('death-fade-complete', { blackOverlay });

                // Resurrection counter — the number and the darkness arrive together.
                // Lingers briefly over the map as the player respawns.
                const counterLife = cfg.fadeToBlackMs + cfg.holdBlackMs;
                const peakAt = 0.35; // fraction of life where alpha hits 1
                const counterText = crispText(
                  scene,
                  SF0_X + GAME_WIDTH / 2, SF0_Y + GAME_HEIGHT / 2,
                  String(deathCount),
                  { fontFamily: Font.family, fontSize: Font.sizeDeathCounter, color: '#ffffff' },
                )
                  .setOrigin(0.5)
                  .setScrollFactor(0)
                  .setDepth(10003)
                  .setAlpha(0);

                const counterScale = counterText.scale;
                scene.tweens.add({
                  targets: counterText,
                  scaleX: counterScale * 1.12,
                  scaleY: counterScale * 1.12,
                  duration: counterLife,
                  ease: 'Sine.easeInOut',
                });
                // Single counter drives alpha — no competing tweens.
                // Quadratic rise to peak, then gentle sublinear fall so
                // it's still visible well into the tail.
                scene.tweens.addCounter({
                  from: 0,
                  to: 1,
                  duration: counterLife,
                  onUpdate: (tween) => {
                    const t = tween.getValue() ?? 0;
                    const alpha = t < peakAt
                      ? Math.pow(t / peakAt, 2)
                      : Math.pow((1 - t) / (1 - peakAt), 0.6);
                    counterText.setAlpha(alpha);
                  },
                  onComplete: () => counterText.destroy(),
                });
              });
            });
          },
        });
      });
    });

    // After manual overlay fade completes -> hold black -> respawn
    scene.events.once('death-fade-complete', ({ blackOverlay }: { blackOverlay: Phaser.GameObjects.Rectangle }) => {
      scene.time.delayedCall(cfg.holdBlackMs * cfg.respawnDelayRatio, () => {
        // Find a safe spawn point — validate before respawning so the player
        // never materializes inside a wall, even for a single frame.
        let spawn = this.deps.getSpawnPoint();
        const geo = this.deps.getPlayerBodyGeometry();
        if (geo) {
          const walkable = wallPhysics.isPositionWalkable(spawn.x, spawn.y);
          const clear = walkable && wallPhysics.isBodyPositionClear(spawn.x, spawn.y, geo);
          if (!clear) {
            const safe = wallPhysics.findNearestClearPosition(spawn.x, spawn.y, geo);
            if (safe) spawn = safe;
          }
        }

        player.respawn(spawn.x, spawn.y);
        player.sprite.setDepth(spawn.y);

        // Reapply passive max HP bonus before saving (respawn heals to max, but max must be correct)
        this.deps.applyMaxHpPassive();

        // Re-save run state at the respawn point with full HP
        saveSystem.saveRun(this.deps.saveName, {
          hp: player.health.hp,
          maxHp: player.health.max,
          position: { x: spawn.x, y: spawn.y },
          zone: CURRENT_ZONE,
          inventory: [],
        });

        // Spawn hidden — physics disabled, sprite invisible until overlay lifts
        player.sprite.setAlpha(0);
        player.body.enable = false;
        scene.events.emit('player-respawned');

        const fadeOutMs = cfg.fadeToBlackMs * cfg.fadeMultiplier;
        // Tell UIScene to fade back in sync with the overlay lifting
        scene.events.emit('death-overlay-out', { fadeOutMs, ease: 'Quad.easeInOut' });

        scene.tweens.add({
          targets: blackOverlay,
          alpha: 0,
          duration: fadeOutMs,
          ease: 'Quad.easeInOut',
          onComplete: () => {
            blackOverlay.destroy();
            deathParticles.coalesce(player.sprite.x, player.sprite.y, 10002).then(() => {
              // Show white silhouette — spawn protection period
              player.sprite.setAlpha(1);
              (player.sprite as Phaser.GameObjects.Sprite).setTintFill(0xffffff);
              this.deps.onStateChange('respawning');

              // After spawn protection expires -> release player
              scene.time.delayedCall(cfg.spawnProtectionMs, () => {
                (player.sprite as Phaser.GameObjects.Sprite).clearTint();
                player.body.enable = true;
                // Re-sync body to sprite position (body drifts while disabled)
                player.body.reset(player.sprite.x, player.sprite.y);
                this.deps.onStateChange('alive');
              });
            });
          },
        });
      });
    });
  }
}
